import { Loading } from "@dataone/lib";
import { Suspense, lazy } from "react";
import { RouteObject, Outlet, Navigate } from "react-router-dom";
import { Layout } from "../layout/Layout";
import { AuthProvider } from "../features/auth/contexts/auth.provider";
import SmartSearchApp from "../features/smart-search/SmartSearchApp";
import ProfileApp from "../features/profile/ProfileApp";
import SupportApp from "../features/support/SupportApp";
import { AnimatedBirdProvider } from "../layout/product-tour/animated-bird.provider";
import { SmartSearchProvider } from "../features/smart-search/contexts/smart-search.provider";
import DesktopFallback from "../layout/desktop-fallback/DesktopFallback";
import AccessDeniedPage from "../features/fallbacks/AccessDeniedPage";
import NotFoundPage from "../features/fallbacks/NotFoundPage";
import { createGlobalStyle } from "styled-components";
import UsagePage from "../features/profile/pages/UsagePage";
import BillingMethodePage from "../features/profile/pages/BillingMethodePage";

const AuthApp = lazy(() => import("../features/auth/AuthApp"));
const DashboardApp = lazy(() => import("../features/dashboard/DashboardApp"));
const DataCenterApp = lazy(
  () => import("../features/data-center/DataCenterApp")
);
const PackageManagementApp = lazy(
  () => import("../features/package-management/PackageManagementApp")
);
const SubscriptionApp = lazy(
  () => import("../features/subscription/SubscriptionApp")
);
const TrialDemoApp = lazy(() => import("../features/trial-demo/TrialDemoApp"));
const WelcomePage = lazy(() => import("../features/welcome/pages/WelcomePage"));
const NotificationStyles = createGlobalStyle`
.ant-notification-notice-wrapper {
  border-top-right-radius: 0 !important;
}

.ant-notification .ant-notification-notice-wrapper .ant-notification-notice::after {
  content: '';
  position: absolute;
  top: -8px;
  right: 0px;
  width: 0;
  height: 0;
  border-left: 10px solid transparent;
  border-bottom: 10px solid #ffffff;
}
`;

export const routes: RouteObject[] = [
  {
    path: "/",
    element: (
      <AuthProvider>
        <SmartSearchProvider>
          <AnimatedBirdProvider>
            <DesktopFallback>
              <Layout>
                <NotificationStyles />
                <SmartSearchApp />
                <Outlet />
              </Layout>
            </DesktopFallback>
          </AnimatedBirdProvider>
        </SmartSearchProvider>
      </AuthProvider>
    ),
    children: [
      {
        index: true,
        element: <Navigate to={"/auth"} />,
      },
      {
        path: `/profile/*`,
        element: <ProfileApp />,
      },
      {
        path: `/usage/*`,
        element: <UsagePage />,
      },
      {
        path: `/billing-methode/*`,
        element: <BillingMethodePage />,
      },
      {
        path: `/support/*`,
        element: <SupportApp />,
      },
      {
        path: `/dashboard/*`,
        element: (
          <Suspense fallback={<Loading />}>
            <DashboardApp />
          </Suspense>
        ),
      },
      {
        path: `/data-management/*`,
        element: (
          <Suspense fallback={<Loading />}>
            <DataCenterApp />
          </Suspense>
        ),
      },
      {
        path: `/package-management/*`,
        element: (
          <Suspense fallback={<Loading />}>
            <PackageManagementApp />
          </Suspense>
        ),
      },
      {
        path: "/access-denied",
        element: (
          <Suspense fallback={<Loading />}>
            <AccessDeniedPage />
          </Suspense>
        ),
      },
    ],
  },
  {
    path: `/auth/*`,
    element: (
      <AuthProvider>
        <DesktopFallback>
          <Suspense fallback={<Loading />}>
            <AuthApp />
          </Suspense>
        </DesktopFallback>
      </AuthProvider>
    ),
  },
  {
    path: `/subscription/*`,
    element: (
      <AuthProvider>
        <Suspense fallback={<Loading />}>
          <SubscriptionApp />
        </Suspense>
      </AuthProvider>
    ),
  },
  {
    path: `/trial/*`,
    element: (
      <AuthProvider>
        <Suspense fallback={<Loading />}>
          <TrialDemoApp />
        </Suspense>
      </AuthProvider>
    ),
  },
  {
    path: "/welcome",
    element: (
      <AuthProvider>
        <DesktopFallback>
          <Suspense fallback={<Loading />}>
            <WelcomePage />
          </Suspense>
        </DesktopFallback>
      </AuthProvider>
    ),
  },
  {
    path: "*",
    element: <NotFoundPage />,
  },
];
